import {
  DndContext,
  DragEndEvent,
  Modifier,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cross2Icon,
  DownloadIcon,
  DragHandleDots2Icon,
} from '@radix-ui/react-icons';
import {
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  IconButton,
  Text,
} from '@radix-ui/themes';
import { MetricHelper } from 'classes/helpers/metric.helper';
import { DetectionFlag } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { AnnotatedVideoPlayer } from 'components/common/video/annotated-player';
import { OutcomeOverrideSelector } from 'components/sections/analytics/outcome-override-selector';
import { DetectionIssueDialog } from 'components/sections/analytics/session-hits/detection-issue-dialog';
import { ResultBadge } from 'components/sections/analytics/session-hits/recap/result-badge';
import { formatRecapValue } from 'components/sections/analytics/session-hits/recap/visualizations/chart-tooltip';
import {
  canFlagDetectionIssue,
  useSessionHitVideo,
} from 'components/sections/analytics/session-hits/use-session-hit-video';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { t } from 'i18next';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { getHideHitDetectionsByOutcome } from 'lib_ts/enums/hitters.enums';
import { expandPitchType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { ISessionHit } from 'lib_ts/interfaces/session-hits/i-session-hit';
import {
  CSSProperties,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const DRAGGABLE_ID = 'session-recap-pitch-card';

/** enough of the header must stay on screen to grab the card again */
const MIN_VISIBLE_PX = 72;
const VIEWPORT_PAD_PX = 8;

/**
 * unlike restrictToWindowEdges, a card taller than the viewport can still move,
 * and the stored offset matches the position the user actually saw
 */
const keepCardReachable: Modifier = ({
  transform,
  draggingNodeRect,
  windowRect,
}) => {
  if (!draggingNodeRect || !windowRect) {
    return transform;
  }

  const nextLeft = draggingNodeRect.left + transform.x;
  const nextTop = draggingNodeRect.top + transform.y;

  const minLeft =
    windowRect.left + VIEWPORT_PAD_PX - draggingNodeRect.width + MIN_VISIBLE_PX;
  const maxLeft =
    windowRect.left + windowRect.width - VIEWPORT_PAD_PX - MIN_VISIBLE_PX;
  const minTop =
    windowRect.top + VIEWPORT_PAD_PX - draggingNodeRect.height + MIN_VISIBLE_PX;
  const maxTop =
    windowRect.top + windowRect.height - VIEWPORT_PAD_PX - MIN_VISIBLE_PX;

  const left = Math.min(Math.max(nextLeft, minLeft), maxLeft);
  const top = Math.min(Math.max(nextTop, minTop), maxTop);

  return {
    ...transform,
    x: transform.x + (left - nextLeft),
    y: transform.y + (top - nextTop),
  };
};

interface IOffset {
  x: number;
  y: number;
}

interface IProps {
  /** filtered hits in table order, for stepping between pitches */
  sortedHits: ISessionHit[];
  byID: Record<string, ISessionRecapHit>;
  szDef: IStrikeZone;
}

const Stat = (props: { label: string; value: ReactNode; unit?: string }) => (
  <Flex direction="column" gap="1">
    <Text size="1" color={RADIX.COLOR.SECONDARY}>
      {t(props.label)}
    </Text>
    <Flex gap="1" align="baseline">
      <Text size="4" weight="bold">
        {props.value}
      </Text>
      {props.unit && (
        <Text size="1" color={RADIX.COLOR.SECONDARY}>
          {props.unit}
        </Text>
      )}
    </Flex>
  </Flex>
);

const CardBody = (props: {
  record: ISessionRecapHit;
  offset: IOffset;
  sortedHits: ISessionHit[];
  szDef: IStrikeZone;
}) => {
  const isUsingVision =
    props.record.hit.tracking_device === TrackingDevice.TrajektVision;

  const { record, sortedHits } = props;
  const { hit, pitch } = record.hit;

  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);
  const current = useAuthStore((s) => s.current);

  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id: DRAGGABLE_ID });

  const video = useSessionHitVideo(record.hit);
  const [playbackUrl, setPlaybackUrl] = useState<string>();

  useEffect(() => setPlaybackUrl(undefined), [video?.videoID]);

  const [dialogDetectionIssue, setDialogDetectionIssue] = useState<number>();

  const index = sortedHits.findIndex((h) => h._id === record.hit._id);

  const select = (h: ISessionHit | undefined) => {
    if (h) {
      useAnalyticsStore.setState({ selectedHitID: h._id });
    }
  };

  const isMetric = sessionMachine?.isMetric;
  const speedUnit = MetricHelper.getSpeedUnit(isMetric);
  const speedFn = isMetric ? MetricHelper.mphToKph : undefined;
  const breakUnit = MetricHelper.getBreaksUnit(isMetric);
  const breakFn = isMetric ? MetricHelper.inToCm : undefined;

  const showDetections =
    hit?._mx_outcome && !getHideHitDetectionsByOutcome(hit._mx_outcome);

  const style: CSSProperties = {
    transform: `translate3d(${props.offset.x + (transform?.x ?? 0)}px, ${
      props.offset.y + (transform?.y ?? 0)
    }px, 0)`,
  };

  const showFlagButton = canFlagDetectionIssue(record.hit, current.role);

  return (
    <Box
      ref={setNodeRef}
      className="recap-pitch-card"
      data-dragging={isDragging}
      style={style}
    >
      <Card size="2">
        <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
          <Flex align="center" gap="2">
            <IconButton
              variant="ghost"
              color={RADIX.COLOR.NEUTRAL}
              size="1"
              className="recap-drag-handle"
              aria-label={t('hitters.recap-drag-card').toString()}
              {...listeners}
              {...attributes}
            >
              <DragHandleDots2Icon />
            </IconButton>

            <Flex direction="column" flexGrow="1" minWidth="0">
              <Heading size="3">
                {t('hitters.recap-pitch-x', { x: record.pitchNumber })}
              </Heading>
              <Text size="1" color={RADIX.COLOR.SECONDARY} truncate>
                {pitch?.name ?? pitch?.type}
              </Text>
            </Flex>

            <IconButton
              variant="ghost"
              color={RADIX.COLOR.NEUTRAL}
              size="1"
              disabled={index <= 0}
              aria-label={t('common.previous').toString()}
              onClick={() => select(sortedHits[index - 1])}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              variant="ghost"
              color={RADIX.COLOR.NEUTRAL}
              size="1"
              disabled={index === -1 || index >= sortedHits.length - 1}
              aria-label={t('common.next').toString()}
              onClick={() => select(sortedHits[index + 1])}
            >
              <ChevronRightIcon />
            </IconButton>
            <IconButton
              variant="ghost"
              color={RADIX.COLOR.NEUTRAL}
              size="1"
              aria-label={t('common.close').toString()}
              onClick={() =>
                useAnalyticsStore.setState({ selectedHitID: null })
              }
            >
              <Cross2Icon />
            </IconButton>
          </Flex>

          <Box className="recap-pitch-card-video">
            {video ? (
              <AnnotatedVideoPlayer
                key={video.videoID}
                details={video}
                onLoadUrl={setPlaybackUrl}
                autoplay
              />
            ) : (
              <Flex
                align="center"
                justify="center"
                className="recap-pitch-card-video-empty"
              >
                <Text size="2" color={RADIX.COLOR.SECONDARY}>
                  {t(
                    isUsingVision
                      ? // vision didn't record a video for some reason
                        'common.no-video-playback-msg'
                      : // vision was not used for this hit
                        'hitters.recap-video-no-vision-tooltip'
                  )}
                </Text>
              </Flex>
            )}
          </Box>

          <Grid columns="2" gapX="4" gapY="3">
            <Stat
              label="common.pitch-type"
              value={pitch?.type ? t(expandPitchType(pitch.type)) : '-'}
            />
            <Stat
              label="hitters.recap-pitch-speed"
              value={formatRecapValue(pitch?.speedMPH, 1, undefined, speedFn)}
              unit={speedUnit}
            />
            <Stat
              label="common.v-break"
              value={formatRecapValue(pitch?.zBreakIN, 1, undefined, breakFn)}
              unit={breakUnit}
            />
            <Stat
              label="common.h-break"
              value={formatRecapValue(
                pitch?.xBreakIN === undefined ? undefined : -pitch.xBreakIN,
                1,
                undefined,
                breakFn
              )}
              unit={breakUnit}
            />
            <Stat
              label="common.exit-velocity"
              value={
                showDetections
                  ? formatRecapValue(hit?.exitMPH, 1, undefined, speedFn)
                  : '-'
              }
              unit={speedUnit}
            />
            <Stat
              label="common.launch-angle"
              value={
                showDetections ? formatRecapValue(hit?.vLaunchDEG, 0) : '-'
              }
              unit="deg"
            />
            <Stat
              label="common.distance"
              value={
                showDetections
                  ? formatRecapValue(
                      hit?.distanceFT,
                      0,
                      undefined,
                      isMetric ? MetricHelper.ftToM : undefined
                    )
                  : '-'
              }
              unit={MetricHelper.getDistanceUnit(isMetric)}
            />
            <Stat
              label="common.result"
              value={
                isUsingVision ? (
                  <OutcomeOverrideSelector
                    hit={record.hit}
                    szDef={props.szDef}
                  />
                ) : (
                  <ResultBadge result={record.result} size="3" weight="bold" />
                )
              }
            />
          </Grid>

          <Flex justify="between" gap="2">
            {showFlagButton ? (
              <Button
                size="1"
                variant="soft"
                color={RADIX.COLOR.NEUTRAL}
                onClick={() => setDialogDetectionIssue(Date.now())}
              >
                <DetectionFlag filled={!!record.hit.detectionIssue} />
                {t('hitters.report-vision-detection-issue')}
              </Button>
            ) : (
              <span />
            )}

            <Button
              size="1"
              variant="soft"
              disabled={!playbackUrl}
              onClick={() => window.open(playbackUrl, '_blank')}
            >
              <DownloadIcon />
              {t('common.export-video')}
            </Button>
          </Flex>
        </Flex>
      </Card>

      {dialogDetectionIssue && (
        <DetectionIssueDialog
          key={dialogDetectionIssue}
          hitID={record.hit._id}
          defaultDetectionIssue={record.hit.detectionIssue}
          onSave={useAnalyticsStore.getState().reportDetectionIssue}
          onClose={() => setDialogDetectionIssue(undefined)}
        />
      )}
    </Box>
  );
};

/** non-modal so the charts and table stay interactive; selecting another pitch swaps the content in place */
export const SessionRecapPitchCard = (props: IProps) => {
  const selectedHitID = useAnalyticsStore((s) => s.selectedHitID);

  // survives closing and reopening so the card returns where the user left it
  const [offset, setOffset] = useState<IOffset>({ x: 0, y: 0 });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  // restrictToWindowEdges changes the live transform but DragEndEvent.delta stays
  // the raw pointer movement, so the card jumped past the clamp and the next drag stuck
  const appliedDelta = useRef({ x: 0, y: 0 });
  const modifiers = useMemo<Modifier[]>(
    () => [
      (args) => {
        const next = keepCardReachable(args);
        appliedDelta.current = { x: next.x, y: next.y };
        return next;
      },
    ],
    []
  );

  useEffect(() => {
    if (!selectedHitID) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        useAnalyticsStore.setState({ selectedHitID: null });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedHitID]);

  const record = selectedHitID ? props.byID[selectedHitID] : undefined;

  if (!record) {
    return null;
  }

  return (
    <ErrorBoundary identifier="SessionRecapPitchCard">
      <DndContext
        sensors={sensors}
        modifiers={modifiers}
        onDragCancel={() => {
          appliedDelta.current = { x: 0, y: 0 };
        }}
        onDragEnd={(_e: DragEndEvent) => {
          const { x, y } = appliedDelta.current;
          appliedDelta.current = { x: 0, y: 0 };
          setOffset((prev) => ({ x: prev.x + x, y: prev.y + y }));
        }}
      >
        <CardBody
          record={record}
          offset={offset}
          sortedHits={props.sortedHits}
          szDef={props.szDef}
        />
      </DndContext>
    </ErrorBoundary>
  );
};
